import type { ReactElement, ReactNode } from 'react';
import { cloneElement, isValidElement, useId } from 'react';
import { cn } from '../lib/cn.js';
import { FORM_PATTERN_MARKERS } from '../forms/catalog.js';

export interface FormFieldProps {
  label: string;
  htmlFor?: string;
  hint?: string;
  error?: string;
  required?: boolean;
  disabled?: boolean;
  loading?: boolean;
  className?: string;
  children: ReactNode;
}

function enhanceControl(
  child: ReactNode,
  ids: { controlId: string; describedBy?: string },
  state: { error?: string; disabled?: boolean; loading?: boolean; required?: boolean },
): ReactNode {
  if (!isValidElement(child)) {
    return child;
  }
  const props: Record<string, unknown> = {
    id: ids.controlId,
    'aria-describedby': ids.describedBy,
  };
  if (state.error) props['aria-invalid'] = true;
  if (state.required) props['aria-required'] = true;
  if (state.disabled) props['aria-disabled'] = true;
  if (state.loading) props['aria-busy'] = true;
  return cloneElement(child as ReactElement<Record<string, unknown>>, props);
}

/**
 * PRM-11 Field — label, control, hint and error as one unit. 07-11: the label
 * is always visible, the hint precedes the control and the error follows it,
 * and required is marked in text rather than by an asterisk alone.
 */
export function FormField({
  label,
  htmlFor,
  hint,
  error,
  required,
  disabled,
  loading,
  className,
  children,
}: FormFieldProps) {
  const uid = useId().replace(/:/g, '');
  const controlId = htmlFor ?? uid;
  const hintId = hint ? `${controlId}-hint` : undefined;
  const errorId = error ? `${controlId}-error` : undefined;
  const describedBy = [hintId, errorId].filter(Boolean).join(' ') || undefined;

  return (
    <div
      data-primitive="PRM-11"
      data-form-pattern={FORM_PATTERN_MARKERS.singleStep}
      className={cn(
        'flex w-full min-w-0 flex-col gap-field-label',
        disabled && 'text-disabled',
        className,
      )}
    >
      <label htmlFor={controlId} className="text-label font-medium text-default">
        {label}
        {required ? <span aria-hidden="true"> *</span> : null}
        {required ? <span className="sr-only"> (required)</span> : null}
      </label>
      {hint ? (
        <p id={hintId} className="text-body-sm text-muted">
          {hint}
        </p>
      ) : null}
      {enhanceControl(children, { controlId, describedBy }, { error, disabled, loading, required })}
      {error ? (
        <p id={errorId} role="alert" className="text-body-sm text-danger">
          {error}
        </p>
      ) : null}
    </div>
  );
}

export function FormSection({
  title,
  description,
  className,
  children,
}: {
  title: string;
  description?: string;
  className?: string;
  children: ReactNode;
}) {
  return (
    <fieldset
      className={cn('m-0 border-0 p-0', className)}
      data-form-pattern={FORM_PATTERN_MARKERS.singleStep}
    >
      <legend className="mb-field-label p-0">
        <span className="text-h3 font-medium text-default">{title}</span>
        {description ? <span className="block text-body-sm text-muted">{description}</span> : null}
      </legend>
      <div className="flex flex-col gap-field">{children}</div>
    </fieldset>
  );
}
